let context: AudioContext | null = null;
let voiceBuffer: AudioBuffer | null = null;
let voiceLoading: Promise<AudioBuffer | null> | null = null;
let generation = 0;
let stopSpeech: (() => void) | null = null;
const sources = new Set<AudioScheduledSourceNode>();
export const REST_VOICE_PHRASE = 'Time to exercise';
export const REST_VOICE_URL = '/audio/time-to-exercise.wav';
export type RestAlertMode = 'voice' | 'soft_chime' | 'off';

function audioContext(): AudioContext | null {
  const Constructor=window.AudioContext??(window as Window & {webkitAudioContext?:typeof AudioContext}).webkitAudioContext;
  if(!Constructor) return null;
  if(!context||context.state==='closed') {context=new Constructor();voiceBuffer=null;voiceLoading=null;}
  return context;
}

async function before<T>(promise:Promise<T>,deadline:number):Promise<T|null> {
  return new Promise(resolve=>{
    const timer=window.setTimeout(()=>resolve(null),Math.max(0,deadline-Date.now()));
    void promise.then(value=>{clearTimeout(timer);resolve(value);},()=>{clearTimeout(timer);resolve(null);});
  });
}

function loadVoice(ctx:AudioContext):Promise<AudioBuffer|null> {
  if(voiceBuffer) return Promise.resolve(voiceBuffer);
  if(voiceLoading) return voiceLoading;
  const controller=new AbortController();
  const pending=before(fetch(REST_VOICE_URL,{cache:'force-cache',signal:controller.signal}).then(response=>{
    if(!response.ok) throw new Error('Voice clip unavailable');
    return response.arrayBuffer();
  }).then(bytes=>ctx.decodeAudioData(bytes)),Date.now()+6000).then(buffer=>{
    if(buffer&&context===ctx) voiceBuffer=buffer;
    return buffer;
  }).finally(()=>{
    controller.abort();
    if(voiceLoading===pending&&!voiceBuffer) voiceLoading=null;
  });
  voiceLoading=pending;
  return pending;
}

function track(source:AudioScheduledSourceNode) {
  sources.add(source);
  source.onended=()=>{sources.delete(source);source.disconnect();};
}

/** Cancel queued/playing cues when moving on; a delayed speech request must never surface later. */
export function cancelRestAlert() {
  generation+=1;
  stopSpeech?.();stopSpeech=null;
  for(const source of sources) {try {source.stop();source.disconnect();} catch {/* already stopped */}}
  sources.clear();
}

function primeSpeech() {
  if(!('speechSynthesis' in window)||typeof SpeechSynthesisUtterance==='undefined') return;
  const synth=window.speechSynthesis;
  const utterance=new SpeechSynthesisUtterance(' ');
  utterance.volume=0;
  let timer:ReturnType<typeof setTimeout>;
  const stop=()=>{clearTimeout(timer);utterance.onend=null;utterance.onerror=null;try{synth.cancel();}catch{}if(stopSpeech===stop)stopSpeech=null;};
  stopSpeech=stop;
  utterance.onend=()=>{clearTimeout(timer);if(stopSpeech===stop)stopSpeech=null;};
  utterance.onerror=utterance.onend;
  timer=setTimeout(stop,250);
  try {synth.getVoices();synth.resume();synth.speak(utterance);} catch {stop();}
}

/** Invoke synchronously from the Done/Preview gesture before any awaits. */
export async function primeRestAlert(mode:RestAlertMode):Promise<void> {
  cancelRestAlert();
  if(mode==='off') return;
  const ctx=audioContext();
  if(mode==='voice'&&!voiceBuffer) primeSpeech();
  if(!ctx) return;
  // Calling resume here, not after fetch/decode, preserves the iOS gesture.
  const resumed=ctx.state==='running'?Promise.resolve():ctx.resume();
  const decoded=mode==='voice'?loadVoice(ctx):Promise.resolve(null);
  await before(Promise.all([resumed,decoded]),Date.now()+2500);
}

export async function primeChime():Promise<void> {await primeRestAlert('soft_chime');}

function chime(ctx:AudioContext,compact=false) {
  const notes=compact?[[523.25,0]] as const:[[523.25,0],[659.25,0.7],[783.99,1.4]] as const;
  for(const [frequency,offset] of notes) {
    const oscillator=ctx.createOscillator();const gain=ctx.createGain();
    const now=ctx.currentTime+offset,duration=compact?0.25:1.25;
    oscillator.type='sine';oscillator.frequency.value=frequency;
    gain.gain.setValueAtTime(0.0001,now);
    gain.gain.exponentialRampToValueAtTime(compact?0.045:0.11,now+0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001,now+duration);
    oscillator.connect(gain).connect(ctx.destination);track(oscillator);
    oscillator.start(now);oscillator.stop(now+duration+0.05);
  }
}

async function speakRestPhrase(token:number,deadline:number):Promise<boolean> {
  if(!('speechSynthesis' in window)||typeof SpeechSynthesisUtterance==='undefined') return false;
  stopSpeech?.();
  const synth=window.speechSynthesis;
  return new Promise(resolve=>{
    let settled=false;
    const utterance=new SpeechSynthesisUtterance(REST_VOICE_PHRASE);
    const voices=synth.getVoices();
    const voice=voices.find(v=>v.localService&&v.lang==='en-AU')??voices.find(v=>v.localService&&v.lang.startsWith('en'))??voices.find(v=>v.lang.startsWith('en'));
    if(voice) utterance.voice=voice;
    utterance.lang=voice?.lang??'en-AU';utterance.rate=0.95;utterance.pitch=1;
    let startup:ReturnType<typeof setTimeout>,completion:ReturnType<typeof setTimeout>;
    const finish=(spoken:boolean,cancel=false)=>{
      if(settled) return;settled=true;clearTimeout(startup);clearTimeout(completion);
      utterance.onstart=null;utterance.onend=null;utterance.onerror=null;
      if(cancel) try{synth.cancel();}catch{}
      if(stopSpeech===stop) stopSpeech=null;resolve(spoken);
    };
    const stop=()=>finish(false,true);stopSpeech=stop;
    utterance.onstart=()=>{if(token!==generation||Date.now()>deadline)stop();else clearTimeout(startup);};
    utterance.onend=()=>finish(true);
    utterance.onerror=()=>finish(false,true);
    startup=setTimeout(stop,Math.max(0,Math.min(1800,deadline-Date.now())));
    completion=setTimeout(stop,7000);
    try {synth.cancel();synth.resume();synth.speak(utterance);} catch {stop();}
  });
}

export async function playGentleChime():Promise<void> {await playRestAlert('soft_chime');}

export async function playRestAlert(mode:RestAlertMode,latestStartAt=Date.now()+4000):Promise<RestAlertMode> {
  cancelRestAlert();
  if(mode==='off'||Date.now()>latestStartAt) return 'off';
  const token=generation;
  const ctx=audioContext();
  if(ctx) {
    const resumed=ctx.state==='running'?true:await before(ctx.resume().then(()=>true),Math.min(latestStartAt,Date.now()+1500));
    if(token!==generation||Date.now()>latestStartAt) return 'off';
    if(resumed&&ctx.state==='running') {
      if(mode==='soft_chime') {chime(ctx);return 'soft_chime';}
      const buffer=await before(loadVoice(ctx),Math.min(latestStartAt,Date.now()+1500));
      if(token!==generation||Date.now()>latestStartAt) return 'off';
      if(buffer) {
        // The fixed phrase uses the same unlocked audio path as its gentle cue.
        // It is independent of asynchronously installed/paused system TTS voices.
        chime(ctx,true);
        const source=ctx.createBufferSource();source.buffer=buffer;
        source.connect(ctx.destination);track(source);
        source.start(ctx.currentTime+0.3);
        return 'voice';
      }
      chime(ctx,true);
    }
  }
  if(mode==='soft_chime') return 'off';
  const spoken=await speakRestPhrase(token,latestStartAt);
  return spoken?'voice':'soft_chime';
}
